import React from 'react';
import { Alert, Button, Card, Descriptions, Space, Spin, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const { Text } = Typography;

type EssTimesheetDay = {
  id?: string;
  attendance_date?: string;
  status?: string;
  check_in?: string | null;
  check_out?: string | null;
  worked_hours?: number | null;
  late_minutes?: number | null;
  project_hours?: { project_code?: string; hours?: number }[];
};

const statusColor = (s?: string) => {
  const v = (s || '').toLowerCase();
  if (v === 'approved') return 'success';
  if (v === 'pendingapproval' || v === 'submitted') return 'processing';
  if (v === 'rejected') return 'error';
  return 'default';
};

const EssTimesheetsPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const { data: rows = [], isLoading: listLoading } = useQuery({
    queryKey: hrmsKeys.essTimesheets(),
    queryFn: () => hrmsService.listEssTimesheets(),
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: hrmsKeys.essTimesheet(id || ''),
    queryFn: () => hrmsService.getEssTimesheet(id!),
    enabled: Boolean(id),
  });

  if (id) {
    return (
      <WorkspaceLayout
        header={
          <EnterprisePageHeader
            documentLabel={detail?.period_label ? `Timesheet ${detail.period_label}` : 'My timesheet'}
            subtitle="Monthly attendance timesheet (read-only)"
            primaryActions={
              <Button onClick={() => navigate('/hr/me/timesheets')}>Back to list</Button>
            }
          />
        }
      >
        {detailLoading ? (
          <Spin />
        ) : !detail ? (
          <Alert type="warning" showIcon message="Timesheet not found" />
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small">
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Period">{detail.period_label}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={statusColor(detail.status)}>{detail.status || '—'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Present">{detail.days_present ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Absent">{detail.days_absent ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Leave">{detail.days_leave ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Worked hours">{detail.total_worked_hours ?? 0}</Descriptions.Item>
                <Descriptions.Item label="OT hours">{detail.total_ot_hours ?? 0}</Descriptions.Item>
              </Descriptions>
            </Card>
            <Table<EssTimesheetDay>
              size="small"
              rowKey={(r, index) => r.id || r.attendance_date || `day-${index}`}
              dataSource={(detail.days || []) as EssTimesheetDay[]}
              pagination={{ pageSize: 31, hideOnSinglePage: true }}
              columns={[
                { title: 'Date', dataIndex: 'attendance_date', width: 120 },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 110,
                  render: (v: string) => <Tag>{v || '—'}</Tag>,
                },
                { title: 'In', dataIndex: 'check_in', width: 90, render: (v) => v || '—' },
                { title: 'Out', dataIndex: 'check_out', width: 90, render: (v) => v || '—' },
                { title: 'Hours', dataIndex: 'worked_hours', width: 80, render: (v) => v ?? '—' },
                { title: 'Late (min)', dataIndex: 'late_minutes', width: 90, render: (v) => v ?? 0 },
                {
                  title: 'Projects',
                  dataIndex: 'project_hours',
                  render: (ph: { project_code?: string; hours?: number }[]) =>
                    Array.isArray(ph) && ph.length
                      ? ph.map((p) => `${p.project_code || '?'}: ${p.hours ?? 0}`).join(', ')
                      : '—',
                },
              ]}
            />
          </Space>
        )}
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="My timesheets"
          subtitle="Your monthly HR attendance timesheets"
          primaryActions={
            <Button href="/hr/me">Back to My HRMS</Button>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="These are your HR monthly timesheets. Project / PMV timesheets are separate."
      />
      {listLoading ? (
        <Spin />
      ) : rows.length === 0 ? (
        <Card>
          <Text type="secondary">No timesheets yet. HR generates months from attendance.</Text>
        </Card>
      ) : (
        <Table
          rowKey="id"
          size="middle"
          dataSource={rows}
          onRow={(r) => ({
            onClick: () => navigate(`/hr/me/timesheets/${r.id}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Period',
              dataIndex: 'period_label',
              render: (v, r) => (
                <Link to={`/hr/me/timesheets/${r.id}`}>{v || `${r.period_month}/${r.period_year}`}</Link>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (v: string) => <Tag color={statusColor(v)}>{v || '—'}</Tag>,
            },
            { title: 'Present', dataIndex: 'days_present', width: 90 },
            { title: 'Absent', dataIndex: 'days_absent', width: 90 },
            { title: 'Leave', dataIndex: 'days_leave', width: 80 },
            { title: 'Hours', dataIndex: 'total_worked_hours', width: 90 },
            { title: 'OT', dataIndex: 'total_ot_hours', width: 70 },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default EssTimesheetsPage;
